import { useState, useCallback } from 'react';
import { useTrip } from '../../contexts/trip-context';
import dayService from '../../../services/day/day.service';

export interface IAddDayModel {
  dayNumber?: number;
  color: string;
  description?: string;
  notes?: string;
  title?: string;
  itineraryId: string;
  tripId: string;
}

export const useAddDay = (
  tripId: string,
  itineraryId: string,
  onSuccess?: () => void
) => {
  const [day, setDay] = useState<IAddDayModel>({
    dayNumber: 1,
    color: '#842234',
    description: '',
    notes: '',
    title: '',
    itineraryId: itineraryId,
    tripId: tripId
  });
  const [validated, setValidated] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setRefreshTrip } = useTrip();

  const handleInputChange = useCallback(
    (field: keyof IAddDayModel, value: any) => {
      setDay((prev) => ({
        ...prev,
        [field]: value
      }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setDay({
      dayNumber: 1,
      color: '#842234',
      description: '',
      notes: '',
      title: '',
      itineraryId: itineraryId,
      tripId: tripId
    });
    setError(null);
    setValidated(true);
  }, [itineraryId, tripId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await dayService.createDay(tripId, itineraryId, day);
      if (result) {
        console.log('Day created successfully:', result);
        setRefreshTrip(true);
        resetForm();
        onSuccess?.();
      } else {
        setError('Day creation failed');
      }
    } catch (error: any) {
      console.error('Error creating day:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create day. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitWithData = async (dayData: {
    color: string;
    title: string;
    description: string;
  }) => {
    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const submitData: IAddDayModel = {
      ...day,
      color: dayData.color,
      title: dayData.title,
      description: dayData.description,
      notes: dayData.description // Use description as notes for now
    };

    try {
      const result = await dayService.createDay(
        tripId,
        itineraryId,
        submitData
      );
      if (result) {
        console.log('Day created successfully:', result);
        setRefreshTrip(true);
        resetForm();
        onSuccess?.();
      } else {
        setError('Day creation failed');
      }
    } catch (error: any) {
      console.error('Error creating day:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create day. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    day,
    validated,
    isSubmitting,
    error,
    handleInputChange,
    handleSubmit,
    handleSubmitWithData,
    resetForm,
    setError
  };
};
